/**
 * NFC Solutions — Session 40.1: offline reference cache for inventory.
 *
 * The stock forms (waste / receiving / transfer / stock take / ingredient
 * edit) need reference data — ingredients, stock locations, balances — to
 * be USABLE when the connection is down. Those are plain GETs, so we keep
 * the last successful response of each in the local IndexedDB `meta`
 * store and fall back to it when the network is unavailable.
 *
 *   cachedGet(http, "/owner/ingredients", { headers })
 *     → { data, fromCache: false }                 (online, cache refreshed)
 *     → { data, fromCache: true, cachedAt }        (offline / network error)
 *     → throws                                     (4xx, or offline with no cache)
 *
 * Balances read from the cache are only ever used as the *expected* value
 * the device saw; the server still decides at sync time whether the real
 * balance moved (stock take / absolute set → sync exception, never a silent
 * overwrite). Nothing here writes stock.
 */
import { putOne, readOne } from "./localDb";
import { DEVICE_ROLES, readRoleSession } from "@/lib/authStorage";

const PREFIX = "ref:";

// Session 44 — cache entries are scoped to the signed-in BUSINESS so a
// device that opens a second business never sees the first one's cached
// ingredients / menu / locations (previously one shared namespace).
function _scope() {
  for (const role of DEVICE_ROLES) {
    try {
      // Device-scoped (confirmed) copy first, then the tab-scoped one —
      // same precedence as before the authStorage consolidation.
      const sess = readRoleSession(role, "device") || readRoleSession(role, "tab");
      if (!sess) continue;
      const b = sess.business;
      const id = (b && (b.id || b.slug)) || sess.business_id;
      if (id) return String(id);
    } catch (_) { /* next */ }
  }
  try { return window.localStorage.getItem("nfcs.business_code") || "anon"; } catch (_) { return "anon"; }
}

function _key(path, params) {
  const p = params && Object.keys(params).length ? `?${Object.keys(params).sort().map((k) => `${k}=${String(params[k])}`).join("&")}` : "";
  return `${PREFIX}${_scope()}:${path}${p}`;
}

function _isOffline() {
  try { return typeof navigator !== "undefined" && navigator.onLine === false; } catch (_) { return false; }
}

function _isNetworkError(err) {
  if (!err) return false;
  if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") return true;
  const status = err.response && err.response.status;
  if (!status) return true;
  return status >= 500;
}

async function _read(key) {
  try {
    const row = await readOne("meta", key);
    if (row && row.data !== undefined) return { data: row.data, fromCache: true, cachedAt: row.ts || null };
  } catch (_) { /* IDB unavailable */ }
  return null;
}

async function _write(key, data) {
  try { await putOne("meta", { key, data, ts: new Date().toISOString() }); } catch (_) { /* best-effort */ }
}

/**
 * GET with offline fallback. Same call signature as `http.get(path, {headers, params})`.
 */
export async function cachedGet(http, path, { headers, params, timeoutMs = 12000 } = {}) {
  const key = _key(path, params);
  if (_isOffline()) {
    const c = await _read(key);
    if (c) return c;
  }
  try {
    const r = await http.get(path, { headers, params, timeout: timeoutMs });
    await _write(key, r.data);
    return { data: r.data, fromCache: false, cachedAt: null };
  } catch (err) {
    if (_isNetworkError(err)) {
      const c = await _read(key);
      if (c) return c;
    }
    throw err;
  }
}

/** Human label for "showing balances as of …". */
export function cachedAtLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return String(iso); }
}
